import React, { useMemo } from 'react';

// Common country codes — short label keeps the select narrow so it fits in modals.
const COUNTRY_CODES: { code: string; label: string; iso: string; flag: string }[] = [
  { code: '+1',   label: '🇺🇸 +1',   iso: 'US', flag: '🇺🇸' },
  { code: '+44',  label: '🇬🇧 +44',  iso: 'GB', flag: '🇬🇧' },
  { code: '+52',  label: '🇲🇽 +52',  iso: 'MX', flag: '🇲🇽' },
  { code: '+61',  label: '🇦🇺 +61',  iso: 'AU', flag: '🇦🇺' },
  { code: '+33',  label: '🇫🇷 +33',  iso: 'FR', flag: '🇫🇷' },
  { code: '+49',  label: '🇩🇪 +49',  iso: 'DE', flag: '🇩🇪' },
  { code: '+34',  label: '🇪🇸 +34',  iso: 'ES', flag: '🇪🇸' },
  { code: '+39',  label: '🇮🇹 +39',  iso: 'IT', flag: '🇮🇹' },
  { code: '+91',  label: '🇮🇳 +91',  iso: 'IN', flag: '🇮🇳' },
  { code: '+81',  label: '🇯🇵 +81',  iso: 'JP', flag: '🇯🇵' },
  { code: '+86',  label: '🇨🇳 +86',  iso: 'CN', flag: '🇨🇳' },
  { code: '+82',  label: '🇰🇷 +82',  iso: 'KR', flag: '🇰🇷' },
  { code: '+55',  label: '🇧🇷 +55',  iso: 'BR', flag: '🇧🇷' },
  { code: '+27',  label: '🇿🇦 +27',  iso: 'ZA', flag: '🇿🇦' },
  { code: '+971', label: '🇦🇪 +971', iso: 'AE', flag: '🇦🇪' },
  { code: '+972', label: '🇮🇱 +972', iso: 'IL', flag: '🇮🇱' },
  { code: '+64',  label: '🇳🇿 +64',  iso: 'NZ', flag: '🇳🇿' },
];

const SUPPORTED_CODES = COUNTRY_CODES.map(c => c.code).sort((a, b) => b.length - a.length);

/** Parse an E.164 (or near-E.164) string into { code, national } pieces.
 *  Falls back to country=+1, national=stripped digits. */
function parsePhone(raw: string): { code: string; national: string } {
  const digits = (raw || '').replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) {
    for (const code of SUPPORTED_CODES) {
      if (digits.startsWith(code)) return { code, national: digits.slice(code.length) };
    }
    // Unknown country code — keep first 1-3 digits as the code.
    const m = digits.match(/^\+(\d{1,3})/);
    if (m) return { code: '+' + m[1], national: digits.slice(m[0].length) };
  }
  // 11-digit US numbers starting with 1
  if (digits.length === 11 && digits.startsWith('1')) {
    return { code: '+1', national: digits.slice(1) };
  }
  return { code: '+1', national: digits };
}

/** Build an E.164 value from country code + national digits. Empty if no national. */
export function toE164(code: string, national: string): string {
  const cleanNational = (national || '').replace(/\D/g, '');
  if (!cleanNational) return '';
  return `${code}${cleanNational}`;
}

/** Best-effort normalize any input string to E.164. Defaults to +1. */
export function normalizePhone(raw: string): string {
  if (!raw) return '';
  const { code, national } = parsePhone(raw);
  return toE164(code, national);
}

interface PhoneInputProps {
  label?: string;
  value: string;
  onChange: (e164: string) => void;
  error?: string;
  required?: boolean;
  defaultCountryCode?: string;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({ label, value, onChange, error, required, defaultCountryCode = '+1' }) => {
  const parsed = useMemo(() => {
    if (!value) return { code: defaultCountryCode, national: '' };
    return parsePhone(value);
  }, [value, defaultCountryCode]);

  const handleCodeChange = (newCode: string) => {
    onChange(toE164(newCode, parsed.national));
  };

  const handleNationalChange = (raw: string) => {
    // Strip everything that isn't a digit. Dashes, dots, spaces, parens — gone.
    const digits = raw.replace(/\D/g, '');
    onChange(toE164(parsed.code, digits));
  };

  return (
    <div className="mb-4">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}{required && ' *'}
        </label>
      )}
      <div className="flex gap-2 min-w-0">
        <select
          className="w-24 flex-shrink-0 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
          value={parsed.code}
          onChange={e => handleCodeChange(e.target.value)}
        >
          {COUNTRY_CODES.map(c => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
        </select>
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          className={`flex-1 min-w-0 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
          value={parsed.national}
          onChange={e => handleNationalChange(e.target.value)}
          onKeyDown={e => {
            // Block dashes, dots, parens, spaces, plus signs at the keystroke level.
            if (['-', '.', '(', ')', ' ', '+'].includes(e.key)) e.preventDefault();
          }}
          placeholder="5551234567"
        />
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};
